import React, { useCallback, useState, useRef, useEffect } from 'react';
import axios from '../axios';
import ReplyItem from './userfeed_reply_item';
import UserfeedSendForm from './userfeed_send_form';
import ActiveUserfeedColumn from './active_userfeed_column';

const FeedList = ({
  currentUser,
  companyId,
  userfeedId,
  hashedCompanyId = null,
  isPrivateRoom = false,
}) => {
  const [userfeed, setUserFeed] = useState([]);
  const [userfeeds, setUserFeeds] = useState([]);
  const [isLoadingNextList, setIsLoadingNextList] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLastPage, setIsLastPage] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReplyId, SetSelectedReplyId] = useState(null);
  const ancLastItemRef = useRef(null);
  const scrollRef = useRef(null);
  const isEnableNextLoad = useRef(true);
  const userfeedsRef = useRef(userfeeds);

  const intervalTime = 5000;

  const getUserName = (userfeed) => {
    let userName = '';

    if (userfeed) {
      if (userfeed.companies_user) {
        const isRealNameRequired =
          userfeed.companies_user.company.is_real_name_required;
        userName = isRealNameRequired
          ? userfeed.companies_user.view_full_name_disp
          : userfeed.companies_user.nickname;
      } else if (userfeed.user && userfeed.user.tunagate_id) {
        userName = userfeed.user.tunagate_id;
      }
    }

    return userName;
  };

  const loadInitialList = useCallback(() => {
    setIsInitialLoading(true);
    setCurrentPage(1);
    axios
      .get(`/api/userfeeds/${userfeedId}`)
      .then((res) => {
        setUserFeed(res.data);
        return axios.get(`/api/userfeeds/${userfeedId}/replies/all`, {
          params: { page: 1, is_private_room: isPrivateRoom },
        });
      })
      .then((res) => {
        setUserFeeds(res.data.sort((a, b) => a.id - b.id));
        setIsLastPage(res.data.length === 0);
      })
      .finally(() => {
        setIsInitialLoading(false);
      });
  }, [isPrivateRoom, userfeedId]);

  const loadNewReply = useCallback(() => {
    axios.get(`/api/userfeeds/${userfeedId}/replies/all`, {
      params: { page: 1, is_private_room: isPrivateRoom },
    }).then((res) => {
      const userfeedIds = userfeedsRef.current.map((item) => item.id);
      const newData = res.data.filter((item) => !userfeedIds.includes(item.id));
      setUserFeeds((prev) => [...prev, ...newData].sort((a, b) => a.id - b.id));
      if (newData.length > 0) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, [isPrivateRoom, userfeedId]);
  // スクロール時
  const loadNextList = useCallback(() => {
    if (isInitialLoading || isLoadingNextList || isLastPage) {
      return;
    }
    const prevScrollHeight = scrollRef.current.scrollHeight;
    const prevScrollTop = scrollRef.current.scrollTop;

    setIsLoadingNextList(true);
    axios
      .get(`/api/userfeeds/${userfeedId}/replies/all`, {
        params: { page: currentPage + 1, is_private_room: isPrivateRoom },
      })
      .then((res) => {
        const userfeedIds = userfeedsRef.current.map((item) => item.id);
        const newData = res.data.filter(
          (item) => !userfeedIds.includes(item.id)
        );
        setUserFeeds((prev) =>
          [...prev, ...newData].sort((a, b) => a.id - b.id)
        );
        setIsLoadingNextList(false);
        setIsLastPage(res.data.length === 0);
        if (!isLastPage) {
          setCurrentPage((prev) => prev + 1);
        }
        scrollRef.current.scrollTop =
          scrollRef.current.scrollHeight - prevScrollHeight + prevScrollTop;
      });
  }, [currentPage, isInitialLoading, isLastPage, isLoadingNextList, isPrivateRoom]);

  useEffect(() => {
    loadInitialList();
    const interval = setInterval(() => {
      const isBottom =
        scrollRef.current.scrollTop + scrollRef.current.clientHeight ===
        scrollRef.current.scrollHeight;
      axios.get(`/api/userfeeds/${userfeedId}/replies/all`, {
        params: { page: 1, is_private_room: isPrivateRoom },
      }).then((res) => {
        const userfeedIds = userfeedsRef.current.map((item) => item.id);
        const newData = res.data.filter(
          (item) => !userfeedIds.includes(item.id)
        );
        setUserFeeds((prev) =>
          [...prev, ...newData].sort((a, b) => a.id - b.id)
        );
        if (newData.length > 0 && isBottom) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    }, intervalTime);

    return () => clearInterval(interval);
  }, [isPrivateRoom, loadInitialList, userfeedId]);

  useEffect(() => {
    userfeedsRef.current = userfeeds;
  }, [userfeeds]);

  useEffect(() => {
    if (!isInitialLoading && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [isInitialLoading]);

  const handleScrollEvent = useCallback(() => {
    if (!ancLastItemRef.current) {
      return;
    }
    const ancPosition = ancLastItemRef.current.offsetTop;
    const scrolled = scrollRef.current.scrollTop;
    if (
      scrolled > ancPosition - 1000 &&
      scrolled < ancPosition &&
      isEnableNextLoad.current
    ) {
      isEnableNextLoad.current = false;
      loadNextList();
    }
  }, [ancLastItemRef, isEnableNextLoad, loadNextList]);

  useEffect(() => {
    if (isLoadingNextList) {
      isEnableNextLoad.current = false;
    } else {
      isEnableNextLoad.current = true;
    }

    if (typeof scrollRef.current !== 'undefined') {
      if (isLastPage) {
        scrollRef.current.removeEventListener('scroll', handleScrollEvent);
      } else {
        scrollRef.current.addEventListener('scroll', handleScrollEvent);
      }

      return () => {
        scrollRef.current.removeEventListener('scroll', handleScrollEvent);
      };
    }
  }, [isLastPage, handleScrollEvent]);
  useEffect(() => {
    axios.post(
      `/api/userfeeds/${userfeedId}/userfeeds_replies_users/bulk_update`,
      { is_private: isPrivateRoom }
    );
  }, [isPrivateRoom, userfeedId]);

  return (
    <React.Fragment>
      <div className="container col-lg-8">
        <div className="row">
          <ActiveUserfeedColumn
            currentUser={currentUser}
            userfeedId={userfeedId}
            companyId={companyId}
            hashedCompanyId={hashedCompanyId}
          />
          <div
            className="container col-md-8 m-0 p-0 bg-white"
            style={{ height: 'calc(100vh - 160px)' }}
          >
            <div
              className="bg-gray d-none d-md-block"
              style={{ height: '34px' }}
            />
            <div
              className="container p-4 justify-content-center"
              style={{
                backgroundImage: `url('${userfeed.image_url_medium}')`,
                backgroundColor: `${
                  userfeed.image_url_medium &&
                  userfeed.image_url_medium.includes(
                    'tunagate.com/images/brown-'
                  )
                    ? 'rgba(0, 0, 0, 0)'
                    : 'rgba(0, 0, 0, 0.5)'
                }`,
                backgroundBlendMode: 'darken',
              }}
            >
              <a
                href={
                  hashedCompanyId
                    ? userfeed.circle_id
                      ? `/community/${hashedCompanyId}/circle/${userfeed.circle_id}/userfeeds`
                      : `/community/${hashedCompanyId}/userfeeds`
                    : '/userfeeds'
                }
                className="text-fz-12px text-white text-fz-bold"
                style={{ textShadow: '0 0 5px #000' }}
              >
                ＜ トークルーム一覧
              </a>
              <div className="">
                <p
                  className="text-fz-16px text-white text-fz-bold m-1"
                  style={{ textShadow: '0 0 5px #000' }}
                >
                  {userfeed.body}
                </p>
              </div>
            </div>
            <div id="ancLastItem" ref={ancLastItemRef} />
            <ul
              id="scroll"
              ref={scrollRef}
              className="list-unstyled m-0 p-2"
              style={{
                height: 'calc(100vh - 290px)',
                overflowY: 'scroll',
              }}
            >
              <li>
                <div className="row justify-content-around align-items-start m-0 mb-3">
                  <div className="col-2 pr-1 pt-4">
                    <img
                      src={userfeed.companies_user
                        ? (userfeed.companies_user ? userfeed.companies_user.avatar_thumb : '' ) 
                        : (userfeed.user ? userfeed.user.avatar_thumb : '')}
                      className="lazyload rounded-circle"
                      alt="userfeed-user-image"
                      width={40}
                    />
                  </div>
                  <div className="col-7 pl-0 pr-1">
                    <div className="d-flex justify-content-between pl-1">
                      <div className="mb-0 pl-0">
                        <p className="text-fz-xs text-black-20 mb-1 line-1 p-0">
                          @{getUserName(userfeed)}
                        </p>
                      </div>
                    </div>
                    <div className="card border-0 bg-gray mb-0 pt-3 pb-3 pr-2 pl-2 text-fz-12px">
                      <p className="mb-0">{userfeed.body}</p>
                    </div>
                  </div>
                  <div className="col-2 p-0 align-self-end">
                    <p className="text-fz-xs mb-0 text-gray">
                      {userfeed.created_at_time_ago_format}
                    </p>
                  </div>
                </div>
              </li>
              {userfeeds.map((reply) => (
                <ReplyItem
                  key={reply.id}
                  currentUser={currentUser}
                  reply={reply}
                  parentReply={userfeeds.find(
                    (r) => r.id === reply.parent_userfeeds_reply_id
                  )}
                  onPressReplyButton={() => SetSelectedReplyId(reply.id)}
                />
              ))}

              {isLoadingNextList ? (
                <div className="d-flex justify-content-center">
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              ) : null}
            </ul>
          </div>
          {hashedCompanyId && (
            <UserfeedSendForm
              userfeedId={userfeedId}
              loadNewReply={loadNewReply}
              isPrivateRoom={isPrivateRoom}
              selectedReply={userfeeds.find(
                (reply) => reply.id === selectedReplyId
              )}
              onPressCloseButton={() => SetSelectedReplyId(null)}
            />
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default FeedList;
